/**
 * Serviço de impressão de relatórios.
 *
 * Engine oficial: Paged.js (paginação CSS Paged Media) + window.print.
 *
 * O utilitário `imprimirPagedJs` encapsula todo o mecanismo já validado no
 * RelatorioPreviewModal: clona o conteúdo do relatório, pagina com Paged.js,
 * injeta o @page dinâmico (preview e impressão) e imprime apenas as folhas
 * geradas — via clone para #rp-print-root (filho direto do <body>).
 *
 * O CSS global de impressão (relatorioPaged.css, importado em main.js) é quem
 * esconde a aplicação e exibe somente #rp-print-root durante o window.print.
 */
import { Previewer } from 'pagedjs'
import {
  gerarCssPage,
  gerarCssPrintPage,
  calcularDimensoesPagina
} from '@/components/relatorios/useRelatorioImagens'

/**
 * Dispara a impressão nativa do navegador.
 * O Paged.js já paginou o conteúdo; o @media print cuida da exibição.
 */
export function imprimirRelatorio () {
  window.print()
}

const CONFIG_PADRAO = {
  pageSize: 'A4',
  pageOrientation: 'portrait',
  pageMargins: [40, 60, 40, 60],
  arquivo: 'relatorio'
}

/**
 * Remove nós de comentário do Vue (<!-- -->) e nós de texto vazio
 * que o Paged.js não consegue processar (nextSignificantNode retorna null).
 * Percorre recursivamente a árvore DOM do conteúdo clonado.
 */
const limparNodosVue = (node) => {
  let child = node.firstChild
  while (child) {
    const next = child.nextSibling
    if (child.nodeType === 8 || // nó de comentário (v-if/v-for/v-show)
        (child.nodeType === 3 && !child.nodeValue?.trim())) { // texto com apenas whitespace
      child.remove()
    } else if (child.nodeType === 1) { // elemento — recursão
      limparNodosVue(child)
    }
    child = next
  }
}

/**
 * Corrige um bug do Paged.js 0.4.x: o reflow pós-render
 * (checkUnderflowAfterResize/checkOverflowAfterResize) chama findEndToken →
 * nodeAfter(null) → "Cannot read properties of null (reading 'nextSibling')".
 * Substituímos esses métodos por no-ops em cada página criada pelo chunker.
 */
const desabilitarReflow = (page) => {
  if (!page) return page
  page.checkUnderflowAfterResize = () => {}
  page.checkOverflowAfterResize = () => {}
  return page
}

const aplicarPatchReflow = (previewer) => {
  const chunker = previewer.chunker
  if (!chunker) return
  if (typeof chunker.addPage === 'function') {
    const origAddPage = chunker.addPage.bind(chunker)
    chunker.addPage = (blank) => desabilitarReflow(origAddPage(blank))
  }
  if (typeof chunker.insertPage === 'function') {
    const origInsertPage = chunker.insertPage.bind(chunker)
    chunker.insertPage = (index, blank) => desabilitarReflow(origInsertPage(index, blank))
  }
  if (typeof chunker.clonePage === 'function') {
    const origClonePage = chunker.clonePage.bind(chunker)
    chunker.clonePage = (originalPage) => desabilitarReflow(origClonePage(originalPage))
  }
}

/**
 * Pagina um elemento já renderizado no DOM com Paged.js e dispara a
 * impressão nativa do navegador (o usuário salva como PDF no diálogo).
 *
 * @param {HTMLElement} elemento - Elemento raiz do relatório (já renderizado)
 * @param {Object} [opcoes]
 * @param {string|number[]} [opcoes.pageSize='A4'] - 'A4' | 'A3' | 'Letter' | 'Legal' | [w,h]
 * @param {string} [opcoes.pageOrientation='portrait'] - 'portrait' | 'landscape'
 * @param {number[]} [opcoes.pageMargins=[40,60,40,60]] - [top, right, bottom, left] em pt
 * @param {string} [opcoes.arquivo='relatorio'] - Nome sugerido do arquivo (document.title)
 * @returns {Promise<void>}
 */
export async function imprimirPagedJs (elemento, opcoes = {}) {
  if (!elemento) {
    console.warn('[imprimirPagedJs] Elemento não informado.')
    return
  }

  const config = { ...CONFIG_PADRAO, ...opcoes }

  // Container de destino (offscreen) onde o Paged.js renderiza as folhas.
  // Precisa estar visível (offsetParent != null) para o Paged.js medir o layout.
  const destino = document.createElement('div')
  destino.className = 'rp-print-dest'
  destino.style.position = 'absolute'
  destino.style.left = '-9999px'
  destino.style.top = '0'
  const { width: larguraPt } = calcularDimensoesPagina(config.pageSize, config.pageOrientation)
  destino.style.width = `${Math.round(larguraPt * 96 / 72)}px`
  document.body.appendChild(destino)

  // Clona o conteúdo do relatório e limpa nós que o Paged.js não processa.
  const conteudo = elemento.cloneNode(true)
  limparNodosVue(conteudo)

  // Neutraliza restrições de layout do container original (ex.: overflow/height
  // de modais, display:none de componentes ocultos) que impediriam o Paged.js
  // de medir e paginar o conteúdo.
  conteudo.style.display = 'block'
  conteudo.style.overflow = 'visible'
  conteudo.style.height = 'auto'
  conteudo.style.maxHeight = 'none'

  // @page do preview (size/margens/numeração) via Blob URL.
  const cssPage = gerarCssPage(config)
  const cssBlob = new Blob([cssPage], { type: 'text/css' })
  const cssUrl = URL.createObjectURL(cssBlob)

  // @page de IMPRESSÃO (size + margin 0) como ÚLTIMO <style> do <head>.
  // O Paged.js injeta o próprio @page durante o preview; se ele vier depois do
  // nosso, a margem do papel volta a ser aplicada e as folhas transbordam.
  const printStyleId = 'rp-print-page-style'
  document.getElementById(printStyleId)?.remove()
  const printStyle = document.createElement('style')
  printStyle.id = printStyleId
  printStyle.textContent = gerarCssPrintPage(config)
  document.head.appendChild(printStyle)

  const previewer = new Previewer()
  aplicarPatchReflow(previewer)

  try {
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await previewer.preview(conteudo, [cssUrl], destino)
  } finally {
    URL.revokeObjectURL(cssUrl)
  }

  const pages = destino.querySelector('.pagedjs_pages')
  if (!pages) {
    console.warn('[imprimirPagedJs] .pagedjs_pages não encontrado após o Paged.js.')
    destino.remove()
    return
  }

  // Clona as folhas para um #rp-print-root filho DIRETO do <body>.
  // O clone preserva os estilos inline do Paged.js e fica fora da árvore do
  // modal — sem ancestrais com display:none/overflow/transform.
  document.getElementById('rp-print-root')?.remove()
  const root = document.createElement('div')
  root.id = 'rp-print-root'
  root.appendChild(pages.cloneNode(true))
  document.body.appendChild(root)

  const tituloAnterior = document.title
  document.title = config.arquivo

  const limpar = () => {
    document.getElementById('rp-print-root')?.remove()
    destino.remove()
    document.title = tituloAnterior
  }
  window.addEventListener('afterprint', limpar, { once: true })

  try {
    imprimirRelatorio()
  } finally {
    // Fallback caso o evento `afterprint` não dispare
    setTimeout(limpar, 2000)
  }
}
